/**
 * staleCompletionsFilter — IJ-238 · Predictive Text defect fix (2026-08-01).
 * ─────────────────────────────────────────────────────────────────
 *
 * Pure function that prunes stale completions kept alive by the
 * SPD v5.87.2 stale-while-revalidate branch in useCompletions.js.
 *
 * Problem the filter solves:
 *   The stale-while-revalidate branch keeps `prev.completions` when
 *   the current trie rank returns []. Over several keystrokes, items
 *   that only ever matched via Tier-3 substring (for example
 *   'world war 2 pacific theatre' surviving the token 'the' because
 *   'theatre' contains 'the') persist into a query that no longer
 *   relates to them at all. The operator's 2026-08-01 screenshot
 *   captured the exact failure on 'the theory of everything'.
 *
 * Contract:
 *   Keep only completions whose normalised text matches the current
 *   partial as either
 *     · Tier-1 full-phrase prefix (partial is a prefix of the text)
 *     · Tier-2 last-word prefix (all-but-last words of the partial
 *       prefix the text AND the last partial word prefixes the next
 *       word of the text)
 *   Anything falling only in Tier-3 substring territory is dropped
 *   as stale. Returns [] when nothing survives.
 *
 * Mirrors rankCompletions Tier-1 / Tier-2 semantics in
 * lib/completionsCorpus.js so the surviving set is exactly what the
 * trie would rank as high-signal for the current partial.
 *
 * Canadian English throughout.
 */

const normalise = (s) => String(s || '').toLowerCase().trim();

/**
 * Return true when `text` matches `partial` as a full-phrase prefix
 * OR as a last-word prefix. Case-insensitive.
 */
export function matchesPartial(text, partial) {
  const t = normalise(text);
  const p = normalise(partial);
  if (!t || !p) return false;
  // Tier 1 — full-phrase prefix
  if (t.startsWith(p)) return true;
  // Tier 2 — last-word prefix
  const words = p.split(/\s+/);
  if (words.length < 2) return false;
  const lastWord = words[words.length - 1];
  const stem = words.slice(0, -1).join(' ');
  if (!t.startsWith(stem)) return false;
  const rest = t.slice(stem.length).trim();
  const firstAfterStem = rest.split(/\s+/)[0] || '';
  return firstAfterStem.startsWith(lastWord);
}

/**
 * Filter an array of completion items (as stored on useCompletions
 * state — objects carrying a `.text` field) by `matchesPartial`.
 * Guards against null / undefined / non-array inputs.
 */
export function filterStaleCompletions(prevCompletions, currentPartial) {
  if (!Array.isArray(prevCompletions)) return [];
  if (!currentPartial) return [];
  return prevCompletions.filter((c) => matchesPartial(c && c.text, currentPartial));
}
